import { createFileRoute } from '@tanstack/react-router'
import {
  ChefHat,
  Copy,
  KeyRound,
  MonitorSmartphone,
  MoreHorizontal,
  Plus,
  ShieldCheck,
  UserRoundCheck,
  UsersRound,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { PageHeader } from '#components/layout/page-header'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '#components/ui/alert-dialog'
import { Badge } from '#components/ui/badge'
import { Button } from '#components/ui/button'
import { Card, CardContent } from '#components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '#components/ui/dialog'
import { Input } from '#components/ui/input'
import { Label } from '#components/ui/label'
import { Skeleton } from '#components/ui/skeleton'
import { Spinner } from '#components/ui/spinner'
import { useCreateDevicePairing } from '#hooks/api/use-device-pairing'
import {
  useArchiveMember,
  useRestoreMember,
  useSaveKitchen,
  useSetMemberActive,
  useTeam,
} from '#hooks/api/use-team'
import type { PairingResponse } from '#services/device-pairing'
import type { TeamMember } from '#services/team'
import {
  formatPairingCountdown,
  getPairingUrl,
  getRemainingPairingSeconds,
} from '#utils/device-pairing'

export const Route = createFileRoute('/_auth/_admin/funcionarios')({
  head: () => ({
    meta: [
      { title: 'Funcionários | UseComanda' },
      {
        name: 'description',
        content: 'Gerencie os acessos da equipe e pareie dispositivos.',
      },
    ],
  }),
  component: TeamPage,
})

type DialogState =
  | { type: 'kitchen'; member?: TeamMember }
  | { type: 'pairing' }
  | null

type AccessFilter = 'active' | 'archived'

function DevicePairingContent({
  data,
  isError,
  isPending,
  onGenerate,
}: {
  data: PairingResponse | undefined
  isError: boolean
  isPending: boolean
  onGenerate: () => void
}) {
  const [remainingSeconds, setRemainingSeconds] = useState(0)

  useEffect(() => {
    if (!data) return

    const updateCountdown = () =>
      setRemainingSeconds(getRemainingPairingSeconds(data.expires_at))
    updateCountdown()
    const interval = window.setInterval(updateCountdown, 1000)

    return () => window.clearInterval(interval)
  }, [data])

  const pairingUrl = data ? getPairingUrl(data.token) : null
  const isExpired = Boolean(data) && remainingSeconds === 0

  if (isPending && !data) {
    return (
      <div
        className="text-muted-foreground flex items-center gap-2 text-sm"
        role="status"
      >
        <Spinner /> Gerando QR…
      </div>
    )
  }

  if (isError || !data || !pairingUrl) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground text-sm">
          Não foi possível gerar o QR de pareamento.
        </p>
        <Button onClick={onGenerate}>Tentar novamente</Button>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="bg-background mx-auto w-fit rounded-xl border p-4">
        <QRCodeSVG value={pairingUrl} size={216} includeMargin />
      </div>
      <div className="space-y-1 text-center">
        <p className="font-medium">{data.tenant.name}</p>
        <p className="text-muted-foreground text-sm">
          {isExpired
            ? 'Este QR expirou.'
            : `Válido por ${formatPairingCountdown(remainingSeconds)}`}
        </p>
      </div>
      <p className="text-muted-foreground text-center text-sm leading-5">
        Escaneie neste novo dispositivo. Depois, o acesso será feito apenas com
        código e PIN.
      </p>
      <div className="flex justify-center gap-2">
        <Button
          className="min-h-11 px-4"
          size="lg"
          type="button"
          variant="outline"
          onClick={() => void navigator.clipboard.writeText(pairingUrl)}
        >
          <Copy /> Copiar link
        </Button>
        <Button className="min-h-11 px-4" size="lg" onClick={onGenerate}>
          {isPending && <Spinner data-icon="inline-start" />}
          {isExpired ? 'Gerar novo QR' : 'Trocar QR'}
        </Button>
      </div>
    </div>
  )
}

function KitchenForm({
  member,
  onClose,
}: {
  member?: TeamMember
  onClose: () => void
}) {
  const [name, setName] = useState(member?.name ?? '')
  const [pin, setPin] = useState('')
  const [role, setRole] = useState<'waiter' | 'kitchen'>(
    member?.role === 'waiter' ? 'waiter' : 'kitchen',
  )
  const save = useSaveKitchen()
  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate(
          { id: member?.id, name, pin: pin || undefined, role },
          { onSuccess: onClose },
        )
      }}
    >
      <div className="space-y-2">
        <Label htmlFor="member-role">Função</Label>
        <select
          id="member-role"
          disabled={Boolean(member)}
          className="border-input bg-background h-10 w-full rounded-lg border px-3 text-sm"
          value={role}
          onChange={(event) =>
            setRole(event.target.value as 'waiter' | 'kitchen')
          }
        >
          <option value="waiter">Garçom</option>
          <option value="kitchen">Cozinha</option>
        </select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="kitchen-name">Nome de identificação</Label>
        <Input
          id="kitchen-name"
          autoFocus
          required
          placeholder="Ex.: Maria ou Cozinha principal"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="kitchen-pin">
          {member ? 'Novo PIN (opcional)' : 'PIN de acesso'}
        </Label>
        <Input
          id="kitchen-pin"
          required={!member}
          minLength={4}
          maxLength={4}
          inputMode="numeric"
          placeholder="••••"
          value={pin}
          onChange={(event) =>
            setPin(event.target.value.replace(/\D/g, '').slice(0, 4))
          }
        />
        <p className="text-muted-foreground text-xs">
          Use quatro números fáceis para a equipe, mas diferentes de senhas
          pessoais.
        </p>
      </div>
      {member?.code && (
        <div className="bg-muted/50 flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-muted-foreground text-xs">Código de acesso</p>
            <p className="font-mono text-base font-semibold tracking-wider">
              {member.code}
            </p>
          </div>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            onClick={() => {
              void navigator.clipboard.writeText(member.code ?? '')
              toast.success('Código copiado.')
            }}
          >
            <Copy />
          </Button>
        </div>
      )}
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button disabled={save.isPending}>
          {save.isPending ? (
            <Spinner data-icon="inline-start" />
          ) : (
            <KeyRound data-icon="inline-start" />
          )}
          {save.isPending
            ? 'Salvando…'
            : member
              ? 'Salvar alterações'
              : 'Criar acesso'}
        </Button>
      </div>
    </form>
  )
}

function TeamPage() {
  const { data = [], isLoading } = useTeam()
  const pairing = useCreateDevicePairing()
  const [dialog, setDialog] = useState<DialogState>(null)
  const [roleFilter, setRoleFilter] = useState<
    'all' | 'admin' | 'waiter' | 'kitchen'
  >('all')
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('active')
  const [archiveDialog, setArchiveDialog] = useState<TeamMember | null>(null)
  const active = useSetMemberActive()
  const archive = useArchiveMember()
  const restore = useRestoreMember()
  const activeMembers = data.filter((member) => !member.archivedAt)
  const archivedMembers = data.filter((member) => member.archivedAt)
  const visibleMembers = (
    accessFilter === 'active' ? activeMembers : archivedMembers
  ).filter((member) => roleFilter === 'all' || member.role === roleFilter)
  const admins = activeMembers.filter((member) => member.role === 'admin')
  const kitchen = activeMembers.filter((member) => member.role === 'kitchen')
  const waiters = activeMembers.filter((member) => member.role === 'waiter')

  return (
    <main className="bg-muted/20 min-h-full px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <PageHeader
          eyebrow="Acessos"
          title="Funcionários"
          subtitle="Crie acessos operacionais e controle quais dispositivos podem entrar no salão."
          actions={
            <>
              <Button
                className="min-h-11 px-5"
                size="lg"
                variant="outline"
                onClick={() => {
                  pairing.reset()
                  pairing.mutate()
                  setDialog({ type: 'pairing' })
                }}
              >
                {pairing.isPending ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <MonitorSmartphone data-icon="inline-start" />
                )}
                {pairing.isPending
                  ? 'Gerando pareamento…'
                  : 'Adicionar dispositivo'}
              </Button>
              <Button
                className="min-h-11 px-5"
                size="lg"
                onClick={() => setDialog({ type: 'kitchen' })}
              >
                <Plus /> Novo acesso
              </Button>
            </>
          }
        />

        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="space-y-4">
            <div>
              <p className="font-semibold">Comece por aqui</p>
              <p className="text-muted-foreground text-sm">
                Em poucos minutos, sua equipe estará pronta para operar.
              </p>
            </div>
            <ol className="grid gap-3 md:grid-cols-3">
              <li className="bg-background rounded-lg border p-3">
                <p className="text-primary text-xs font-semibold">
                  1. Crie o acesso
                </p>
                <p className="mt-1 text-sm">
                  Escolha garçom ou cozinha, informe o nome e defina um PIN de
                  quatro dígitos.
                </p>
              </li>
              <li className="bg-background rounded-lg border p-3">
                <p className="text-primary text-xs font-semibold">
                  2. Entregue o código
                </p>
                <p className="mt-1 text-sm">
                  Copie o código gerado e compartilhe-o apenas com a pessoa
                  responsável.
                </p>
              </li>
              <li className="bg-background rounded-lg border p-3">
                <p className="text-primary text-xs font-semibold">
                  3. Pareie o dispositivo
                </p>
                <p className="mt-1 text-sm">
                  Abra o QR neste computador ou celular; depois, a equipe entra
                  só com código e PIN.
                </p>
              </li>
            </ol>
          </CardContent>
        </Card>

        <div className="grid gap-3 sm:grid-cols-3">
          <Card size="sm">
            <CardContent className="flex items-center gap-3">
              <span className="bg-primary/10 text-primary grid size-10 place-items-center rounded-lg">
                <UsersRound />
              </span>
              <div>
                <p className="text-muted-foreground text-xs">
                  Equipe cadastrada
                </p>
                <p className="text-xl font-semibold">{activeMembers.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card size="sm">
            <CardContent className="flex items-center gap-3">
              <span className="bg-muted grid size-10 place-items-center rounded-lg">
                <ShieldCheck />
              </span>
              <div>
                <p className="text-muted-foreground text-xs">Administradores</p>
                <p className="text-xl font-semibold">{admins.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card size="sm">
            <CardContent className="flex items-center gap-3">
              <span className="bg-muted grid size-10 place-items-center rounded-lg">
                <ChefHat />
              </span>
              <div>
                <p className="text-muted-foreground text-xs">Operação</p>
                <p className="text-xl font-semibold">
                  {kitchen.length + waiters.length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="gap-0 py-0">
          <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-semibold">
                {accessFilter === 'active'
                  ? 'Pessoas e acessos'
                  : 'Acessos arquivados'}
              </h2>
              <p className="text-muted-foreground text-sm">
                {accessFilter === 'active'
                  ? 'Revise periodicamente quem pode entrar no sistema.'
                  : 'Consulte ou restaure acessos removidos da operação.'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="bg-muted flex w-fit rounded-lg p-1">
                {(
                  [
                    ['active', 'Ativos'],
                    ['archived', 'Arquivados'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${accessFilter === value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setAccessFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="bg-muted flex w-fit rounded-lg p-1">
                {(
                  [
                    ['all', 'Todos'],
                    ['admin', 'Admins'],
                    ['waiter', 'Garçons'],
                    ['kitchen', 'Cozinha'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${roleFilter === value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setRoleFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
              <Skeleton className="h-14" />
            </div>
          ) : visibleMembers.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-14 text-center">
              <span className="bg-muted mb-4 grid size-12 place-items-center rounded-xl">
                <UserRoundCheck />
              </span>
              <h3 className="font-medium">
                {accessFilter === 'active'
                  ? 'Sua equipe começa aqui'
                  : 'Nenhum acesso arquivado'}
              </h3>
              <p className="text-muted-foreground mt-1 max-w-md text-sm">
                {accessFilter === 'active'
                  ? 'Crie um acesso com PIN para que sua equipe comece a operar.'
                  : 'Os acessos arquivados aparecerão aqui para consulta ou restauração.'}
              </p>
              {accessFilter === 'active' && (
                <Button
                  className="mt-4 min-h-11 px-5"
                  size="lg"
                  onClick={() => setDialog({ type: 'kitchen' })}
                >
                  <Plus /> Criar primeiro acesso
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {visibleMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-4 px-4 py-3"
                >
                  <span
                    className={`grid size-10 shrink-0 place-items-center rounded-full ${member.role === 'admin' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}
                  >
                    {member.role === 'admin' ? <ShieldCheck /> : <ChefHat />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-medium">{member.name}</p>
                      <Badge
                        variant={
                          member.archivedAt
                            ? 'outline'
                            : member.isActive
                              ? 'secondary'
                              : 'outline'
                        }
                      >
                        {member.archivedAt
                          ? 'Arquivado'
                          : member.isActive
                            ? 'Ativo'
                            : 'Suspenso'}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {member.role === 'admin'
                        ? 'Administrador · acesso por e-mail'
                        : `${member.role === 'waiter' ? 'Garçom' : 'Cozinha'} · código ${member.code ?? 'em processamento'}`}
                    </p>
                  </div>
                  {member.role !== 'admin' && member.code && (
                    <Button
                      className="hidden min-h-10 px-3 sm:flex"
                      variant="ghost"
                      onClick={() => {
                        void navigator.clipboard.writeText(member.code ?? '')
                        toast.success('Código copiado.')
                      }}
                    >
                      <Copy /> Copiar código
                    </Button>
                  )}
                  {!member.archivedAt && (
                    <Button
                      className="size-10"
                      disabled={active.isPending}
                      size="icon"
                      variant="ghost"
                      onClick={() =>
                        member.role !== 'admin'
                          ? setDialog({ type: 'kitchen', member })
                          : active.mutate({
                              id: member.id,
                              isActive: !member.isActive,
                            })
                      }
                    >
                      {active.isPending ? <Spinner /> : <MoreHorizontal />}
                    </Button>
                  )}
                  {member.role !== 'admin' && (
                    <div className="flex gap-1">
                      {member.archivedAt ? (
                        <Button
                          className="min-h-10 px-3"
                          disabled={restore.isPending}
                          variant="ghost"
                          onClick={() => restore.mutate({ id: member.id })}
                        >
                          {restore.isPending && (
                            <Spinner data-icon="inline-start" />
                          )}
                          Restaurar acesso
                        </Button>
                      ) : (
                        <>
                          <Button
                            className="min-h-10 px-3"
                            disabled={active.isPending}
                            variant="ghost"
                            onClick={() =>
                              active.mutate({
                                id: member.id,
                                isActive: !member.isActive,
                              })
                            }
                          >
                            {active.isPending && (
                              <Spinner data-icon="inline-start" />
                            )}
                            {active.isPending
                              ? 'Atualizando…'
                              : member.isActive
                                ? 'Suspender'
                                : 'Ativar'}
                          </Button>
                          <Button
                            className="min-h-10 px-3"
                            variant="ghost"
                            onClick={() => setArchiveDialog(member)}
                          >
                            Arquivar acesso
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Dialog
          open={dialog !== null}
          onOpenChange={(open) => !open && setDialog(null)}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {dialog?.type === 'pairing'
                  ? 'Adicionar dispositivo'
                  : dialog?.member
                    ? 'Editar acesso da cozinha'
                    : 'Novo acesso da cozinha'}
              </DialogTitle>
              <DialogDescription>
                {dialog?.type === 'pairing'
                  ? 'O QR funciona uma única vez e expira em cinco minutos.'
                  : 'O código é gerado automaticamente e o PIN nunca fica visível.'}
              </DialogDescription>
            </DialogHeader>
            {dialog?.type === 'pairing' ? (
              <DevicePairingContent
                data={pairing.data}
                isError={pairing.isError}
                isPending={pairing.isPending}
                onGenerate={() => pairing.mutate()}
              />
            ) : dialog?.type === 'kitchen' ? (
              <KitchenForm
                member={dialog.member}
                onClose={() => setDialog(null)}
              />
            ) : null}
          </DialogContent>
        </Dialog>
        <AlertDialog
          open={archiveDialog !== null}
          onOpenChange={(open) => !open && setArchiveDialog(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Arquivar acesso?</AlertDialogTitle>
              <AlertDialogDescription>
                {archiveDialog ? (
                  <>
                    <strong>{archiveDialog.name}</strong> perderá o acesso
                    imediatamente. O histórico das comandas será preservado e o
                    acesso poderá ser restaurado depois.
                  </>
                ) : null}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                disabled={archive.isPending}
                onClick={(event) => {
                  if (!archiveDialog) return
                  event.preventDefault()
                  archive.mutate(
                    { id: archiveDialog.id },
                    { onSuccess: () => setArchiveDialog(null) },
                  )
                }}
              >
                {archive.isPending && <Spinner data-icon="inline-start" />}
                {archive.isPending ? 'Arquivando…' : 'Arquivar acesso'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </main>
  )
}
